export const getSafe = (fn, defaultVal) => {
    try {
        return fn();
    } catch (e) {
        return defaultVal;
    }
};

export const getSafeOrDefault = (fn, defaultVal) => {
    return getSafe(fn, defaultVal) || defaultVal;
};

export const getByPath = (obj, path) => {
    return path.split(".").reduce((p, c) => (p && p[c]) || null, obj);
};

export const getByPathOrDefault = (obj, path, defaultValue) => {
    return getByPath(obj, path) || defaultValue;
};

// Caution: This method modifies the local item object with the remote values.
export const mergeCollectionsById = (localItems, remoteItems, idField) => {
    const remoteCopy = [...remoteItems];
    localItems.forEach((s) => {
        const remoteIndex = remoteCopy.findIndex(
            (rs) => rs[idField] === s[idField]
        );
        if (remoteIndex >= 0) {
            const [remoteItem] = remoteCopy.splice(remoteIndex, 1);
            Object.assign(s, remoteItem);
        }
    });
    return [...localItems, ...remoteCopy];
};

export const addDays = (date, days) => {
    var result = new Date(date);
    result.setDate(result.getDate() + days);

    return result;
};

export const pick = (obj, ...keys) => {
    return obj
        ? keys.reduce((a, x) => {
              if (obj.hasOwnProperty(x)) a[x] = obj[x];
              return a;
          }, {})
        : null;
};

export const deepCopy = (obj) => {
    if (obj == null || typeof obj !== "object") {
        return obj;
    }

    const temp = new obj.constructor();

    Object.keys(obj).forEach((key) => {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            temp[key] = deepCopy(obj[key]);
        }
    });

    return temp;
};

export const isObject = function (a) {
    return (
        a && typeof a === "object" && !isString(a) && !isDate(a) && !isArray(a)
    );
};

export const isDate = function (a) {
    return a && a.constructor.name === "Date";
};

export const isString = function (a) {
    return a && a.constructor.name === "String";
};

export const isArray = function (a) {
    return Array.isArray(a);
};

export const isNumber = function (a) {
    return typeof a === "number" && !Number.isNaN(a);
};

export const isDefined = function (a) {
    return typeof a !== "undefined" && a !== null;
};

export const isoDateComparer = (a, b) => {
    return new Date(a).getTime() - new Date(b).getTime();
};

export const toNumber = (value) => {
    return value ? Number(value) : null;
};

export const camelToUnderscore = (str) => {
    return str
        .split(/\.?(?=[A-Z])/)
        .join("_")
        .toLowerCase();
};

/**
 * Deep value equality for config values: arrays element-wise, plain objects
 * key-wise (order-insensitive), everything else strict. Object elements used
 * to be compared by reference, which flagged untouched marker arrays
 * (fieldAreaSet) as changed in the settings change summary.
 */
export const areEqual = (o1, o2) => {
    let result;
    if (isArray(o1) && isArray(o2)) {
        result =
            o1.length === o2.length && o1.every((ov, i) => areEqual(ov, o2[i]));
    } else if (isObject(o1) && isObject(o2)) {
        const keys1 = Object.keys(o1);
        result =
            keys1.length === Object.keys(o2).length &&
            keys1.every((key) => areEqual(o1[key], o2[key]));
    } else {
        result = o1 === o2;
    }
    return result;
};

export const deepAssign = (target, source) => {
    for (const key in source) {
        if (source.hasOwnProperty(key)) {
            if (typeof source[key] === "object" && source[key] !== null) {
                if (!target[key]) {
                    target[key] = Array.isArray(source[key]) ? [] : {};
                }
                deepAssign(target[key], source[key]);
            } else {
                target[key] = source[key];
            }
        }
    }
    return target;
};

/**
 * Returns a sequentially incremented array of numbers of size length, and beginning from the provided start
 * @param {number} length
 * @param {number} start
 * @returns {number[]}
 */
export const fillArray = (length, start) => {
    return Array.from({ length: length }, (_, i) => i + start);
};
